import React from 'react';
import { Stack, ToggleButton, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useTranslation } from 'react-i18next';
import type { TutorSessionType } from '../../models/Tutor';
import {
  getTutorSessionTypeLabel,
  TUTOR_SESSION_TYPES,
} from '../../utils/tutorSessionType';

const CONTROL_H = 42;

const choiceButtonSx = {
  border: '1px solid rgba(255, 255, 255, 0.14)',
  borderRadius: '10px',
  px: 1.25,
  minHeight: CONTROL_H,
  fontSize: { xs: '0.78rem', sm: '0.82rem' },
  fontWeight: 600,
  letterSpacing: '0.01em',
  color: 'rgba(255,255,255,0.62)',
  textTransform: 'none',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  justifyContent: 'flex-start',
  gap: 0.75,
  transition: 'background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease',
  '&.Mui-selected': {
    color: '#E8ECFF',
    backgroundColor: 'rgba(78, 103, 235, 0.28)',
    borderColor: 'rgba(78, 103, 235, 0.7)',
    '&:hover': {
      backgroundColor: 'rgba(78, 103, 235, 0.34)',
    },
  },
  '&:hover:not(.Mui-selected)': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.22)',
  },
} as const;

function sessionTypeIcon(type: TutorSessionType) {
  if (type === 'ONLINE') return <VideocamOutlinedIcon sx={{ fontSize: 18, opacity: 0.9 }} />;
  if (type === 'AT_STUDENT') return <HomeOutlinedIcon sx={{ fontSize: 18, opacity: 0.9 }} />;
  return <PlaceOutlinedIcon sx={{ fontSize: 18, opacity: 0.9 }} />;
}

export interface TutorSessionTypeChoiceListProps {
  selectedTypes: TutorSessionType[];
  onChanged: (next: TutorSessionType[]) => void;
  /** When false, tapping selects exactly one type (search filters). Default true. */
  multiSelect?: boolean;
  enabled?: boolean;
  /** Horizontal chip row (search toolbar) vs stacked rows (listing editor). */
  direction?: 'row' | 'column';
  'aria-label'?: string;
}

/**
 * Session-type choice chips — parity with mobile `TutorSessionTypeChoiceList`.
 * Multiselect toggles membership; single-select always keeps exactly one type.
 */
const TutorSessionTypeChoiceList: React.FC<TutorSessionTypeChoiceListProps> = ({
  selectedTypes,
  onChanged,
  multiSelect = true,
  enabled = true,
  direction = 'column',
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();

  const handleTap = (type: TutorSessionType) => {
    if (!enabled) return;
    if (multiSelect) {
      const next = selectedTypes.includes(type)
        ? selectedTypes.filter((value) => value !== type)
        : [...selectedTypes, type];
      onChanged(TUTOR_SESSION_TYPES.filter((value) => next.includes(value)));
      return;
    }
    onChanged([type]);
  };

  return (
    <Stack
      direction={direction}
      alignItems={direction === 'row' ? 'center' : 'stretch'}
      spacing={direction === 'row' ? 0.5 : 0.75}
      flexWrap={direction === 'row' ? 'wrap' : undefined}
      useFlexGap={direction === 'row'}
      role="group"
      aria-label={ariaLabel}
      sx={{ flexShrink: 0, width: direction === 'row' ? { xs: '100%', md: 'auto' } : '100%' }}
    >
      {TUTOR_SESSION_TYPES.map((type) => {
        const selected = selectedTypes.includes(type);
        return (
          <ToggleButton
            key={type}
            value={type}
            selected={selected}
            disabled={!enabled}
            onClick={() => handleTap(type)}
            aria-label={type}
            sx={{
              ...choiceButtonSx,
              flex: direction === 'row' ? { xs: '1 1 auto', md: '0 0 auto' } : undefined,
              width: direction === 'column' ? '100%' : undefined,
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
              {sessionTypeIcon(type)}
              <Typography
                component="span"
                noWrap
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  fontSize: 'inherit',
                  fontWeight: selected ? 700 : 500,
                  color: 'inherit',
                }}
              >
                {getTutorSessionTypeLabel(type, t)}
              </Typography>
              {selected ? <CheckRoundedIcon sx={{ fontSize: 18, color: 'primary.light', flexShrink: 0 }} /> : null}
            </Stack>
          </ToggleButton>
        );
      })}
    </Stack>
  );
};

export default TutorSessionTypeChoiceList;
